/**
 * V4RunsPanel — Phase 7q-1.
 *
 * Left-panel runs list, the V3-parity "Execution" tab. Lives inside
 * V4LeftPanel and swaps with V4NodePalette via the left-rail mode
 * icons. Lazy-fetches on mount + polls while there is at least one
 * non-terminal run (so a fresh kickoff appears without a manual
 * refresh).
 *
 * Phase scope:
 *   7q-1 (this file's initial form)
 *       • Basic list: status pill, run id, started-at, duration,
 *         click → host's onSelectRun(runId)
 *       • Newest-first sort, polling
 *   7q-2 will add origin filter pills (Canvas / Agent / Schedule / API)
 *        and color-coded left borders per origin.
 *   7q-3 will add per-row Stop / Terminate / Resume controls.
 *
 * Contract: presentational + own fetch. Host owns the openRunId.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  History, Loader2, RefreshCw, MousePointerClick, Bot, Calendar, Globe,
  Pause, Square, Play, AlertOctagon,
} from 'lucide-react';
import * as api from '../../services/api';
import {
  S, BRAND, ACCENT, SPACING, RADIUS, FONT, WEIGHT,
  ghostBtn, RUN_STATUS, statusPillStyle,
} from './v4Tokens';

// Phase 7q-3 — actionable statuses. Drives which row buttons render.
const STOPPABLE = new Set(['RUNNING', 'PAUSED']);
const RESUMABLE = new Set(['PENDING_APPROVAL', 'PAUSED']);
const TERMINATABLE = new Set(['RUNNING', 'PAUSED', 'PENDING_APPROVAL']);
// Confirm-on-second-click window (ms) for Terminate. Re-clicking the
// red chip within this window executes; otherwise the chip resets.
const CONFIRM_WINDOW_MS = 3000;

const PANEL_WIDTH = 240;
const POLL_MS = 3000;
const TERMINAL_STATUSES = new Set([
  'COMPLETED', 'COMPLETED_WITH_ERRORS', 'FAILED', 'TERMINATED', 'TIMED_OUT',
]);
const MAX_VISIBLE = 50;

/* ───── Origin classification — PARITY-V3 ─────────────────────────── *
 * V3's logic (CanvasPageV3.jsx:5800-5818):
 *   • triggerType field on each run classifies it
 *   • manual / undefined → "Canvas" (operator-initiated from the canvas)
 *   • agentlet            → "Agent"
 *   • schedule            → "Schedule"
 *   • api                 → "API"
 * Color-coded left borders match the V3 palette so operators recognise
 * the surface at a glance. We use V4 tokens for parity with the rest
 * of the chrome.                                                      */
const ORIGINS = [
  {
    key: 'all',
    label: 'All',
    icon: null,
    tone: { fg: S[700], bg: S[100], border: S[400] },
    match: () => true,
  },
  {
    key: 'manual',
    label: 'Canvas',
    icon: MousePointerClick,
    tone: { fg: S[700], bg: S[100], border: S[400] },
    match: (r) => !r.triggerType || r.triggerType === 'manual',
  },
  {
    key: 'agentlet',
    label: 'Agent',
    icon: Bot,
    tone: { fg: BRAND.dark, bg: BRAND.light, border: BRAND.icon },
    match: (r) => r.triggerType === 'agentlet' || r.triggerType === 'agent',
  },
  {
    key: 'schedule',
    label: 'Schedule',
    icon: Calendar,
    tone: { fg: ACCENT.amber.text, bg: ACCENT.amber.bg, border: ACCENT.amber.icon },
    match: (r) => r.triggerType === 'schedule' || r.triggerType === 'scheduled',
  },
  {
    key: 'api',
    label: 'API',
    icon: Globe,
    tone: { fg: ACCENT.sky.text, bg: ACCENT.sky.bg, border: ACCENT.sky.icon },
    match: (r) => r.triggerType === 'api',
  },
];

const ORIGIN_BY_KEY = Object.fromEntries(ORIGINS.map((o) => [o.key, o]));

// Classify a single run → origin entry. Falls back to "manual" for
// any unknown triggerType so the row still renders with a sensible
// border colour.
function classifyOrigin(run) {
  for (const o of ORIGINS) {
    if (o.key === 'all') continue;
    if (o.match(run)) return o;
  }
  return ORIGIN_BY_KEY.manual;
}

function fmtAgo(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const ms = Date.now() - d.getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  if (ms < 30 * 86_400_000) return `${Math.round(ms / 86_400_000)}d ago`;
  return d.toLocaleDateString();
}

function fmtDuration(startTs, endTs) {
  if (!startTs) return '';
  const start = new Date(startTs).getTime();
  if (!Number.isFinite(start)) return '';
  const end = endTs ? new Date(endTs).getTime() : Date.now();
  const ms = end - start;
  if (ms < 0) return '';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 60_000)}m`;
}

export default function V4RunsPanel({
  workflowId,
  // Most-recent run id from the host (set by handleRun). When this
  // changes we refresh immediately AND visually flag the row.
  freshRunId,
  // Currently-selected run id — used to highlight the active row so the
  // operator can correlate the list with the bottom-dock detail view.
  openRunId,
  // Click handler — host opens the bottom-dock detail panel.
  onSelectRun,
  // Phase 7q-3 — toast surface forwarded from the host so per-row
  // Stop / Terminate / Resume can report success or failure without
  // V4RunsPanel needing its own toast stack.
  onToast,
}) {
  const [runs, setRuns] = useState(null); /* null = not loaded */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Phase 7q-2 — origin filter. Persists in-memory only; not worth
  // localStorage given how rarely operators want a sticky filter.
  const [originFilter, setOriginFilter] = useState('all');
  // Phase 7q-3 — per-row state: which run is currently confirming
  // a Terminate (resets after CONFIRM_WINDOW_MS), and which runs have
  // an action in flight so we can disable buttons + show a spinner.
  const [confirmingTerminate, setConfirmingTerminate] = useState(null);
  const [actionsInFlight, setActionsInFlight] = useState(new Set());
  const confirmTimerRef = useRef(null);
  const pollTimerRef = useRef(null);

  const fetchAll = useMemo(() => async () => {
    if (!workflowId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getWorkflowRuns(workflowId);
      const payload = (res && typeof res === 'object' && 'data' in res) ? res.data : res;
      const list = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.items) ? payload.items : []);
      // Newest-first. The BE typically returns this order but be defensive.
      const sorted = list.slice().sort((a, b) => {
        const ta = new Date(a.startedAt || a.createdAt || 0).getTime();
        const tb = new Date(b.startedAt || b.createdAt || 0).getTime();
        return tb - ta;
      });
      setRuns(sorted);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  // Initial fetch + on workflow change + on freshRunId change.
  useEffect(() => {
    if (!workflowId) return undefined;
    fetchAll();
    return () => {};
  }, [workflowId, freshRunId, fetchAll]);

  // Poll while there is at least one non-terminal run. Stops when
  // everything's done so we don't hammer the BE for idle workflows.
  useEffect(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (!workflowId || !Array.isArray(runs) || runs.length === 0) return undefined;
    const hasActive = runs.some((r) => !TERMINAL_STATUSES.has(String(r.status || '').toUpperCase()));
    if (!hasActive) return undefined;
    pollTimerRef.current = setTimeout(() => { fetchAll(); }, POLL_MS);
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [runs, workflowId, fetchAll]);

  // Apply the origin filter BEFORE the visible-cap so the cap doesn't
  // hide a filtered-in run that's further down the unfiltered list.
  const filtered = useMemo(() => {
    if (!runs) return [];
    const f = ORIGIN_BY_KEY[originFilter] || ORIGIN_BY_KEY.all;
    return runs.filter((r) => f.match(r));
  }, [runs, originFilter]);

  const visible = useMemo(() => filtered.slice(0, MAX_VISIBLE), [filtered]);
  const hasRuns = (runs || []).length > 0;
  const hasFilteredRuns = filtered.length > 0;

  // Per-origin counts for the pill chips (lets the operator see at a
  // glance how many runs each origin has without flipping filters).
  const originCounts = useMemo(() => {
    const counts = { all: (runs || []).length };
    for (const o of ORIGINS) {
      if (o.key === 'all') continue;
      counts[o.key] = (runs || []).filter((r) => o.match(r)).length;
    }
    return counts;
  }, [runs]);

  // Phase 7q-3 — Stop / Terminate / Resume.
  //
  //   Stop      → api.cancelWorkflowRun     (graceful, keeps history)
  //   Terminate → api.terminateWorkflowRun  (forceful, two-click confirm)
  //   Resume    → api.resumeWorkflowRun     (PAUSED / PENDING_APPROVAL)
  //
  // After every action we re-fetch so the row's status pill updates
  // immediately (and the row's button surface re-renders).
  const toast = (msg, tone = 'info') => {
    if (typeof onToast === 'function') onToast(msg, tone);
  };

  const markInFlight = (runId, flag) => {
    setActionsInFlight((prev) => {
      const next = new Set(prev);
      if (flag) next.add(runId); else next.delete(runId);
      return next;
    });
  };

  const runAction = async (runId, kind) => {
    if (!runId) return;
    markInFlight(runId, true);
    try {
      let res;
      if (kind === 'stop') {
        res = await api.cancelWorkflowRun(runId);
      } else if (kind === 'terminate') {
        res = await api.terminateWorkflowRun(runId);
      } else if (kind === 'resume') {
        res = await api.resumeWorkflowRun(runId, {});
      } else {
        throw new Error(`Unknown action: ${kind}`);
      }
      const ok = res && (res.ok === true || res.ok === undefined);
      if (!ok && res && res.error) {
        toast(`${labelFor(kind)} failed: ${res.error}`, 'error');
      } else {
        toast(`${labelFor(kind)} ${String(runId).slice(0, 8)}…`, 'success');
      }
      await fetchAll();
    } catch (e) {
      toast(`${labelFor(kind)} failed: ${e?.message || String(e)}`, 'error');
    } finally {
      markInFlight(runId, false);
    }
  };

  const handleStop = (runId) => runAction(runId, 'stop');
  const handleResume = (runId) => runAction(runId, 'resume');

  // Two-click confirm for terminate. First click arms (red chip);
  // second click within CONFIRM_WINDOW_MS executes. Auto-cancels if
  // the operator walks away.
  const handleTerminate = (runId) => {
    if (confirmingTerminate === runId) {
      // Confirmed — execute.
      if (confirmTimerRef.current) {
        clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = null;
      }
      setConfirmingTerminate(null);
      runAction(runId, 'terminate');
      return;
    }
    // First click — arm.
    setConfirmingTerminate(runId);
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    confirmTimerRef.current = setTimeout(() => {
      setConfirmingTerminate(null);
      confirmTimerRef.current = null;
    }, CONFIRM_WINDOW_MS);
  };

  useEffect(() => () => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
  }, []);

  return (
    <aside
      role="region"
      aria-label="Workflow runs"
      style={{
        width: PANEL_WIDTH,
        flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        background: '#ffffff',
        borderRight: `0.5px solid ${S[200]}`,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: SPACING.xs + 2,
        padding: `${SPACING.s + 2}px ${SPACING.m}px`,
        borderBottom: `0.5px solid ${S[200]}`,
      }}>
        <History size={12} aria-hidden="true" style={{ color: S[600] }} />
        <span style={{
          fontSize: FONT.sm, fontWeight: WEIGHT.bold, color: S[800],
          textTransform: 'uppercase', letterSpacing: 0.4,
        }}>
          Runs
        </span>
        {hasRuns && (
          <span style={{
            fontSize: FONT.xs, fontWeight: WEIGHT.semibold, color: S[500],
          }}>
            {(runs || []).length}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {loading && (
          <Loader2 size={11} aria-label="Loading" className="v4-spin" style={{ color: S[500] }} />
        )}
        <button
          type="button"
          onClick={fetchAll}
          disabled={loading || !workflowId}
          aria-label="Refresh runs"
          title="Refresh runs"
          style={{ ...ghostBtn, padding: 2 }}
        >
          <RefreshCw size={11} style={{ opacity: loading ? 0.4 : 1 }} aria-hidden="true" />
        </button>
      </div>

      {/* Phase 7q-2 — origin filter pills. Always visible (even when
       * no runs yet) so the operator sees what filters exist before
       * the first run. Pills wrap to 2 rows on narrow widths. */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 4,
        padding: `${SPACING.xs + 2}px ${SPACING.s + 2}px`,
        borderBottom: `0.5px solid ${S[100]}`,
      }}>
        {ORIGINS.map((o) => {
          const active = originFilter === o.key;
          const count = originCounts[o.key] || 0;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => setOriginFilter(o.key)}
              aria-pressed={active}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '2px 6px',
                fontSize: FONT.xs, fontWeight: WEIGHT.semibold,
                color: active ? o.tone.fg : S[600],
                background: active ? o.tone.bg : 'transparent',
                border: active
                  ? `0.5px solid ${o.tone.border}`
                  : `0.5px solid ${S[200]}`,
                borderRadius: RADIUS.pill,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
              title={`${o.label} runs${count > 0 ? ` (${count})` : ''}`}
            >
              {o.icon && <o.icon size={9} aria-hidden="true" />}
              {o.label}
              {count > 0 && (
                <span style={{
                  marginLeft: 1, fontWeight: WEIGHT.medium,
                  color: active ? o.tone.fg : S[400],
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {error && (
          <div style={{
            padding: SPACING.l, fontSize: FONT.sm,
            color: ACCENT.rose.text, background: ACCENT.rose.bg,
            margin: SPACING.m, borderRadius: RADIUS.md,
          }}>
            Couldn&apos;t load runs: {error}
          </div>
        )}
        {!error && runs !== null && !hasRuns && !loading && (
          <Placeholder>
            No runs yet. Click <strong>Run</strong> in the toolbar to start one.
          </Placeholder>
        )}
        {!error && runs === null && loading && (
          <Placeholder>Loading runs…</Placeholder>
        )}
        {!error && hasRuns && !hasFilteredRuns && (
          <Placeholder>
            No <strong>{ORIGIN_BY_KEY[originFilter]?.label || originFilter}</strong> runs.
            {' '}<a href="#all" onClick={(e) => { e.preventDefault(); setOriginFilter('all'); }}
              style={{ color: ACCENT.sky.text, textDecoration: 'none' }}>Show all</a>.
          </Placeholder>
        )}
        {!error && hasFilteredRuns && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {visible.map((r) => {
              const status = String(r.status || '').toUpperCase();
              const tone = RUN_STATUS[status]
                || { ...ACCENT.slate, label: status || 'Unknown' };
              const isFresh = freshRunId && r.id === freshRunId;
              const isOpen = openRunId && r.id === openRunId;
              const origin = classifyOrigin(r);
              return (
                <li key={r.id} style={{ borderBottom: `0.5px solid ${S[100]}` }}>
                  <button
                    type="button"
                    onClick={() => { if (typeof onSelectRun === 'function') onSelectRun(r.id); }}
                    style={{
                      display: 'flex', flexDirection: 'column', gap: 3,
                      width: '100%', padding: '7px 10px',
                      background: isOpen
                        ? S[50]
                        : (isFresh ? '#ecfeff' : '#ffffff'),
                      border: 'none',
                      /* Left border encodes origin (V3 parity). Open /
                       * fresh state still wins so the operator can spot
                       * their active run regardless of origin. */
                      borderLeft: isOpen || isFresh
                        ? `3px solid ${ACCENT.sky.icon}`
                        : `3px solid ${origin.tone.border}`,
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={statusPillStyle(tone)}>{tone.label}</span>
                      <code style={{
                        fontSize: FONT.xs, color: S[500],
                        fontFamily: 'SFMono-Regular, Menlo, monospace',
                      }}>
                        {String(r.id || '').slice(0, 8)}
                      </code>
                      <span style={{ flex: 1 }} />
                      <span style={{ fontSize: FONT.xs, color: S[400] }}>
                        {fmtAgo(r.startedAt || r.createdAt)}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      fontSize: FONT.xs, color: S[600],
                    }}>
                      {/* Origin chip — small, colored to match the
                       * left border so the row reads at a glance. */}
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 2,
                        fontSize: FONT.xs, fontWeight: WEIGHT.semibold,
                        color: origin.tone.fg,
                      }}>
                        {origin.icon && <origin.icon size={9} aria-hidden="true" />}
                        {origin.label}
                      </span>
                      {r.startedAt && (
                        <span style={{ color: S[500] }}>· {fmtDuration(r.startedAt, r.completedAt)}</span>
                      )}
                      {r.triggeredBy && (
                        <span style={{
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          flex: 1, color: S[500],
                        }}>· {r.triggeredBy}</span>
                      )}
                      <RowActions
                        runId={r.id}
                        status={status}
                        inFlight={actionsInFlight.has(r.id)}
                        confirmingTerminate={confirmingTerminate === r.id}
                        onStop={handleStop}
                        onTerminate={handleTerminate}
                        onResume={handleResume}
                      />
                    </div>
                    {r.errorMessage && (
                      <span style={{
                        fontSize: FONT.xs, color: ACCENT.rose.text,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {r.errorMessage}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}

function Placeholder({ children }) {
  return (
    <div style={{
      padding: SPACING.l, textAlign: 'center',
      fontSize: FONT.sm, color: S[500], lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
}

/* ───── Phase 7q-3 — per-row action buttons ───────────────────────── *
 * Renders Stop / Terminate / Resume icons appropriate to the run's
 * status. Stops propagation so clicking an action button doesn't also
 * fire the row's onSelectRun. Terminate uses two-click confirm — the
 * button morphs into a red "Confirm?" chip with the same on-click,
 * second click executes.                                              */
function RowActions({
  runId, status, inFlight, confirmingTerminate,
  onStop, onTerminate, onResume,
}) {
  const stop = (e, fn) => { e.preventDefault(); e.stopPropagation(); fn(runId); };
  const canStop = STOPPABLE.has(status);
  const canTerminate = TERMINATABLE.has(status);
  const canResume = RESUMABLE.has(status);

  if (!canStop && !canTerminate && !canResume) return null;
  if (inFlight) {
    return <Loader2 size={11} className="v4-spin" aria-label="Working" style={{ color: S[400] }} />;
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      {canResume && (
        <button
          type="button"
          onClick={(e) => stop(e, onResume)}
          aria-label={`Resume run ${String(runId).slice(0, 8)}`}
          title="Resume"
          style={iconBtnStyle(ACCENT.emerald.icon)}
        >
          <Play size={10} aria-hidden="true" />
        </button>
      )}
      {canStop && (
        <button
          type="button"
          onClick={(e) => stop(e, onStop)}
          aria-label={`Stop run ${String(runId).slice(0, 8)}`}
          title="Stop (graceful)"
          style={iconBtnStyle(S[600])}
        >
          <Pause size={10} aria-hidden="true" />
        </button>
      )}
      {canTerminate && (
        confirmingTerminate ? (
          <button
            type="button"
            onClick={(e) => stop(e, onTerminate)}
            aria-label="Confirm terminate"
            title="Click again to confirm terminate"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 2,
              padding: '1px 5px',
              fontSize: FONT.xs, fontWeight: WEIGHT.semibold,
              color: ACCENT.rose.text, background: ACCENT.rose.bg,
              border: `0.5px solid ${ACCENT.rose.icon}`,
              borderRadius: RADIUS.pill,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <AlertOctagon size={9} aria-hidden="true" />
            Confirm?
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => stop(e, onTerminate)}
            aria-label={`Terminate run ${String(runId).slice(0, 8)}`}
            title="Terminate (destructive)"
            style={iconBtnStyle(ACCENT.rose.icon)}
          >
            <Square size={10} aria-hidden="true" />
          </button>
        )
      )}
    </span>
  );
}

function iconBtnStyle(color) {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 16, height: 16, padding: 0,
    color,
    background: 'transparent',
    border: 'none', borderRadius: RADIUS.sm,
    cursor: 'pointer', fontFamily: 'inherit',
  };
}

function labelFor(kind) {
  if (kind === 'stop') return 'Stopped';
  if (kind === 'terminate') return 'Terminated';
  if (kind === 'resume') return 'Resumed';
  return kind;
}
